<template>
  <div class="file-upload-wrapper">
    <el-upload
      class="upload-demo"
      drag
      action="#"
      :auto-upload="false"
      :on-change="handleFileSelect"
      :file-list="fileList"
      :accept="accept"
      :disabled="isUploading"
      :before-remove="handleBeforeRemove"
      :limit="limit"
      :on-exceed="handleExceed"
      :multiple="limit !== 1"
    >
      <i class="el-icon-upload"></i>
      <div class="el-upload__text">将文件拖到此处，或<em>点击选择</em></div>
      <div class="el-upload__tip" slot="tip">
        {{ tipText }}
      </div>
    </el-upload>

    <!-- 上传进度 -->
    <div class="upload-progress" v-for="(fileObj, index) in files" :key="index" v-if="showProgress">
      <div class="file-info">
        <span class="file-name">{{ fileObj.name }}</span>
        <span class="file-size">{{ formatFileSize(fileObj.size) }}</span>
      </div>
      
      <el-progress
        :percentage="Math.round(fileObj.progress * 100)"
        :status="fileObj.uploadSuccess ? 'success' : undefined"
        :stroke-width="18"
      >
        <template slot="default">
          <span class="progress-label">
            {{ fileObj.isCalculatingMd5 ? '计算MD5中...' : 
               fileObj.uploadSuccess ? '上传完成' : 
               isUploading ? '上传中...' : '准备上传' }}
          </span>
        </template>
      </el-progress>

      <div class="status-info">
        <el-tag :type="fileObj.statusType" size="small">{{ fileObj.statusText }}</el-tag>
        <span class="chunk-info" v-if="fileObj.totalChunks > 0">
          分片进度：{{ fileObj.uploadedChunks.size }}/{{ fileObj.totalChunks }}
        </span>
      </div>
    </div>

    <!-- 操作按钮 -->
    <div class="action-buttons" v-if="showActionButtons && files.length > 0 && !isUploading">
      <el-button
        type="primary"
        size="small"
        :loading="isUploading"
        @click="startUpload"
      >
        {{ isUploading ? '上传中...' : '开始上传' }}
      </el-button>
      <el-button
        type="danger"
        size="small"
        :disabled="!isUploading"
        @click="cancelUpload"
      >
        取消上传
      </el-button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import SparkMD5 from 'spark-md5';
import { getToken } from '@/utils/auth';

export default {
  name: 'FileUpload',
  props: {
    // 接受的文件类型
    accept: {
      type: String,
      default: 'video/*'
    },
    // 提示文本
    tipText: {
      type: String,
      default: '支持大文件上传（1G-5G），自动分片上传'
    },
    // 分片大小（默认 100MB）
    chunkSize: {
      type: Number,
      default: 100 * 1024 * 1024
    },
    // 并发上传数
    concurrent: {
      type: Number,
      default: 4
    },
    // 上传接口基础 URL
    baseUrl: {
      type: String,
      default: '/api/file/upload'
    },
    // 是否显示进度
    showProgress: {
      type: Boolean,
      default: true
    },
    // 额外的上传参数
    extraParams: {
      type: Object,
      default: () => ({})
    },
    // 是否自动上传（文件选择后自动开始）
    autoUpload: {
      type: Boolean,
      default: false
    },
    // 是否显示操作按钮
    showActionButtons: {
      type: Boolean,
      default: true
    },
    // 是否自动计算 MD5（文件选择后自动开始）
    autoCalculateMd5: {
      type: Boolean,
      default: true
    },
    // 上传数量限制
    limit: {
      type: Number,
      default: 0 // 0 表示不限制
    }
  },
  data() {
    return {
      fileList: [],
      files: [], // 存储多个文件的上传状态
      isUploading: false,
      activeRequests: 0,
      cancelTokenSource: null,
      axiosInstance: null
    };
  },
  created() {
    this.initAxios();
  },
  methods: {
    initAxios() {
      this.axiosInstance = axios.create({
        timeout: 60000
      });

      // 添加请求拦截器，自动添加 token
      this.axiosInstance.interceptors.request.use(
        (config) => {
          const token = getToken();
          if (token) {
            config.headers['Authorization'] = token;
          }
          return config;
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    },

    handleExceed(files, fileList) {
      if (this.limit === 1) {
        this.$message.warning(`当前限制选择 1 个文件，请先删除已有文件后再选择`);
      } else {
        this.$message.warning(`当前限制选择 ${this.limit} 个文件，本次选择了 ${files.length} 个文件，共选择了 ${files.length + fileList.length} 个文件`);
      }
    },

    handleFileSelect(file, fileList) {
      if (!file || !file.raw) return;

      this.fileList = fileList;
      
      // 智能分片算法：内网 100MB，公网 10MB
      const hostname = window.location.hostname;
      const isInternal = /^(localhost|127\.0\.0\.1|192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(hostname);
      const activeChunkSize = isInternal ? 100 * 1024 * 1024 : 10 * 1024 * 1024;
      console.log(`[环境感知] 当前主机: ${hostname}, 模式: ${isInternal ? '内网' : '公网'}, 分片大小: ${activeChunkSize / 1024 / 1024}MB`);

      // 检查文件是否已存在
      const existingFile = this.files.find(f => f.name === file.name);
      if (!existingFile) {
        const fileObj = {
          file: file.raw,
          name: file.name,
          size: file.size,
          order: this.files.length + 1,
          fileMd5: '',
          activeChunkSize: activeChunkSize, // 存储该文件使用的分片大小
          totalChunks: Math.ceil(file.raw.size / activeChunkSize),
          uploadedChunks: new Set(),
          progress: 0,
          isCalculatingMd5: false,
          uploadSuccess: false,
          statusText: `已选择文件：${file.name}`,
          statusType: 'info'
        };
        console.log('添加文件:', fileObj);
        this.files.push(fileObj);
        
        this.$emit('file-selected', {
          file: fileObj.file,
          totalChunks: fileObj.totalChunks
        });

        // 根据配置决定是否自动计算MD5
        if (this.autoCalculateMd5) {
          this.calculateFileMd5(fileObj);
        }
      }
    },

    handleBeforeRemove(file, fileList) {
      if (this.isUploading) {
        this.$message.warning('文件正在上传中，无法删除');
        return false;
      }
      // 从 files 数组中删除对应的文件
      const index = this.files.findIndex(f => f.name === file.name);
      if (index > -1) {
        this.files.splice(index, 1);
      }
      this.fileList = fileList;
      return true;
    },

    resetState() {
      this.fileList = [];
      this.files = [];
      this.isUploading = false;
      this.activeRequests = 0;
    },

    // 计算文件MD5（极速采样模式，与 Java 客户端同步）
    // 计算文件MD5（极速采样模式，与 Java 客户端及后端同步）
    async calculateFileMd5(fileObj) {
      fileObj.isCalculatingMd5 = true;
      fileObj.statusText = '正在计算文件特征...';
      fileObj.statusType = 'info';

      try {
        const file = fileObj.file;
        const fileSize = file.size;
        const spark = new SparkMD5.ArrayBuffer();
        const readSize = 1024 * 1024; // 采样块大小 1MB

        // 辅助读取函数
        const readBlob = (start, end) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e) => resolve(e.target.result);
            reader.onerror = reject;
            reader.readAsArrayBuffer(file.slice(start, end));
          });
        };

        if (fileSize <= 3 * 1024 * 1024) {
          // 小文件全量计算
          const data = await readBlob(0, fileSize);
          spark.append(data);
        } else {
          // 大文件采样计算：头 1MB + 中 1MB + 尾 1MB + 文件大小
          // 1. 头部
          const head = await readBlob(0, readSize);
          spark.append(head);
          
          // 2. 中间
          const midPos = Math.floor(fileSize / 2);
          const mid = await readBlob(midPos, midPos + readSize);
          spark.append(mid);
          
          // 3. 尾部
          const tail = await readBlob(fileSize - readSize, fileSize);
          spark.append(tail);
          
          // 4. 文件大小特征（确保与 Java 端及后端字符串拼接逻辑一致）
          const sizeBuffer = new TextEncoder().encode(fileSize.toString()).buffer;
          spark.append(sizeBuffer);
        }

        fileObj.fileMd5 = spark.end();
        fileObj.isCalculatingMd5 = false;
        fileObj.progress = 0;
        fileObj.statusText = `视频特征提取完成`;
        fileObj.statusType = 'success';

        console.log(`[极速MD5] 文件: ${file.name}, 结果: ${fileObj.fileMd5}`);

        this.$emit('md5-calculated', {
          md5: fileObj.fileMd5,
          extraParams: this.extraParams
        });

        // 检查已上传的分片
        await this.checkUploadedChunks(fileObj);
      } catch (error) {
        fileObj.isCalculatingMd5 = false;
        fileObj.statusText = `视频特征提取失败：${error.message}`;
        fileObj.statusType = 'error';
        this.$message.error('视频特征提取失败');
      }
    },

    // 检查已上传的分片
    async checkUploadedChunks(fileObj) {
      fileObj.statusText = '正在检查已上传分片...';
      try {
        const token = getToken();
        
        // 构建查询参数
        const params = {
          fileMd5: fileObj.fileMd5,
          fileCount: this.files.length,
          ...this.extraParams
        };
        
        console.log('检查分片请求参数:', params);
        
        const response = await this.axiosInstance.post(
          `${this.baseUrl}/check`,
          null,
          {
            params: params
          }
        );

        const result = response.data;
        console.log('检查分片响应结果:', result)
        // 如果返回-1表示文件已存在
        if (result && result.includes(-1)) {
          fileObj.progress = 1;
          fileObj.uploadSuccess = true;
          fileObj.statusText = '文件已存在，无需重复上传';
          fileObj.statusType = 'success';
          this.$emit('upload-success', {
            fileMd5: fileObj.fileMd5,
            fileName: fileObj.file.name,
            fileSize: fileObj.file.size,
            response: result,
            extraParams: this.extraParams
          });
        } else if (result && Array.isArray(result)) {
          fileObj.uploadedChunks = new Set(result);
          fileObj.progress = fileObj.uploadedChunks.size / fileObj.totalChunks;
          fileObj.statusText = `检测到已上传分片：${fileObj.uploadedChunks.size}/${fileObj.totalChunks}`;
          fileObj.statusType = 'warning';
        } else {
          fileObj.statusText = '准备开始上传';
          fileObj.statusType = 'info';
        }
      } catch (error) {
        console.warn('分片检查失败，将从头开始上传:', error);
        fileObj.statusText = '准备开始上传';
        fileObj.statusType = 'info';
      }
    },

    // 手动添加文件（用于独立实例）
    addFileManually(fileObj) {
      if (!fileObj || !fileObj.file) return;
      
      const hostname = window.location.hostname;
      const isInternal = /^(localhost|127\.0\.0\.1|192\.168\.|10\.|172\.(1[6-9]|2[0-9]|3[0-1])\.)/.test(hostname);
      const activeChunkSize = isInternal ? 100 * 1024 * 1024 : 10 * 1024 * 1024;

      const existingFile = this.files.find(f => f.name === fileObj.name);
      if (!existingFile) {
        const newFileObj = {
          file: fileObj.file,
          name: fileObj.name,
          size: fileObj.size,
          order: this.files.length + 1,
          fileMd5: '',
          activeChunkSize: activeChunkSize,
          totalChunks: Math.ceil(fileObj.file.size / activeChunkSize),
          uploadedChunks: new Set(),
          progress: 0,
          isCalculatingMd5: false,
          uploadSuccess: false,
          statusText: `已选择文件：${fileObj.name}`,
          statusType: 'info'
        };
        this.files.push(newFileObj);
        
        this.$emit('file-selected', {
          file: newFileObj.file,
          totalChunks: newFileObj.totalChunks
        });
      }
    },
    addFilesManually(fileList) {
      if (!fileList || !Array.isArray(fileList)) return;
      fileList.forEach(fileObj => {
        this.addFileManually(fileObj);
      });
    },
    getAggregatedProgress() {
      if (this.files.length === 0) return 0;
      if (this.files.length === 1) return this.files[0].progress || 0;
      let totalWeight = 0;
      let weightedSum = 0;
      this.files.forEach(f => {
        const weight = f.file ? f.file.size : 1;
        totalWeight += weight;
        weightedSum += (f.progress || 0) * weight;
      });
      return totalWeight > 0 ? weightedSum / totalWeight : 0;
    },

    // 手动开始计算 MD5
    async startCalculateMd5() {
      if (this.files.length === 0) {
        this.$message.warning('请先选择文件');
        return;
      }

      for (const fileObj of this.files) {
        if (!fileObj.fileMd5) {
          await this.calculateFileMd5(fileObj);
        }
      }
      
      // 所有文件 MD5 计算完成
      this.$emit('all-md5-calculated', {
        fileCount: this.files.length,
        extraParams: this.extraParams
      });
    },

    // 开始上传
    async startUpload() {
      if (this.files.length === 0) {
        this.$message.warning('请先选择文件');
        return;
      }

      if (this.isUploading) {
        this.$message.info('上传已开始');
        return;
      }

      this.isUploading = true;
      this.cancelTokenSource = axios.CancelToken.source();

      this.$emit('upload-start', { extraParams: this.extraParams });

      // 初始化速度计算
      this._lastLoadedBytes = this.getAggregatedProgress() * this.files.reduce((sum, f) => sum + (f.file ? f.file.size : 0), 0);
      this.speedInterval = setInterval(() => {
        const currentProgress = this.getAggregatedProgress();
        const totalSize = this.files.reduce((sum, f) => sum + (f.file ? f.file.size : 0), 0);
        const currentLoadedBytes = currentProgress * totalSize;
        const diff = currentLoadedBytes - this._lastLoadedBytes;
        const speed = diff > 0 ? diff : 0;
        this._lastLoadedBytes = currentLoadedBytes;

        const speedText = this.formatFileSize(speed) + '/s';
        
        this.$emit('upload-progress', {
          progress: currentProgress,
          speedText: speedText,
          extraParams: this.extraParams
        });
      }, 1000);

      try {
        // 并发上传多个文件
        const uploadPromises = this.files.map(fileObj => this.uploadSingleFile(fileObj));
        await Promise.all(uploadPromises);

        const params = {
          ...this.extraParams
        };

        let mergeResponse = null;
        console.log('多视频合并请求参数:', params);
        const token = getToken();
        mergeResponse = await axios.post(
          `${this.baseUrl}/multipleMerge`,
          null,
          {
            params: params,
            headers: { 
              'Authorization': token
            }
          }
        );
        
        // 抛出最终合并成功的事件
        this.$emit('upload-success', {
          isBatch: true,
          response: mergeResponse.data,
          extraParams: this.extraParams
        });
        this.isUploading = false;
      } catch (error) {
        this.isUploading = false;
        this.$emit('upload-error', error);
      } finally {
        if (this.speedInterval) {
          clearInterval(this.speedInterval);
          this.speedInterval = null;
        }
      }
    },

    // 上传单个文件
    async uploadSingleFile(fileObj) {
      if (!fileObj.file || !fileObj.fileMd5) {
        return;
      }

      if (fileObj.uploadSuccess) {
        return;
      }

      fileObj.statusText = '开始上传分片...';
      fileObj.statusType = 'info';

      try {
        const totalChunks = fileObj.totalChunks;
        const chunkIndices = [];
        for (let i = 0; i < totalChunks; i++) {
          if (!fileObj.uploadedChunks.has(i)) {
            chunkIndices.push(i);
          }
        }

        if (chunkIndices.length === 0) {
          fileObj.progress = 1;
          await this.mergeChunks(fileObj, this.files.length);
          return;
        }

        // 并发控制：Worker 模式
        const worker = async () => {
          while (chunkIndices.length > 0) {
            const index = chunkIndices.shift();
            this.activeRequests++;
            try {
              await this.uploadSingleChunk(fileObj, index);
            } finally {
              this.activeRequests--;
            }
          }
        };

        // 启动指定数量的并发 Worker
        const workers = [];
        const concurrency = Math.min(this.concurrent, chunkIndices.length);
        for (let i = 0; i < concurrency; i++) {
          workers.push(worker());
        }

        // 等待所有 Worker 完成
        await Promise.all(workers);

        // 所有分片上传完成，开始合并
        if (this.isUploading) {
          await this.mergeChunks(fileObj, this.files.length);
        }
      } catch (error) {
        fileObj.statusText = `上传失败：${error.message}`;
        fileObj.statusType = 'error';
        throw error;
      }
    },

    // 上传单个分片
    async uploadSingleChunk(fileObj, chunkIndex) {
      const start = chunkIndex * fileObj.activeChunkSize;
      const end = Math.min(start + fileObj.activeChunkSize, fileObj.file.size);
      const chunk = fileObj.file.slice(start, end);

      const formData = new FormData();
      formData.append('fileMd5', fileObj.fileMd5);
      formData.append('fileName', fileObj.file.name);
      formData.append('fileSize', fileObj.file.size);
      formData.append('chunkIndex', chunkIndex);
      formData.append('totalChunks', fileObj.totalChunks);
      formData.append('file', chunk);

      // 添加额外参数
      Object.keys(this.extraParams).forEach(key => {
        formData.append(key, this.extraParams[key]);
      });

      try {
        await this.axiosInstance.post(
          `${this.baseUrl}/chunk`,
          formData,
          {
            cancelToken: this.cancelTokenSource.token,
            onUploadProgress: (progressEvent) => {
              const chunkProgress = progressEvent.loaded / progressEvent.total;
              const totalProgress = (
                fileObj.uploadedChunks.size + chunkProgress
              ) / fileObj.totalChunks;
              fileObj.progress = totalProgress;
            }
          }
        );

        fileObj.uploadedChunks.add(chunkIndex);
        fileObj.progress = fileObj.uploadedChunks.size / fileObj.totalChunks;
        fileObj.statusText = `上传进度：${fileObj.uploadedChunks.size}/${fileObj.totalChunks}`;

        this.$emit('chunk-uploaded', {
          chunkIndex,
          uploadedCount: fileObj.uploadedChunks.size,
          totalChunks: fileObj.totalChunks,
          extraParams: this.extraParams,
          progress: this.files.length > 1 ? this.getAggregatedProgress() : (fileObj.uploadedChunks.size / fileObj.totalChunks),
          fileCount: this.files.length
        });
      } catch (error) {
        if (axios.isCancel(error)) {
          throw new Error('上传已取消');
        }
        // 重试机制
        console.warn(`分片 ${chunkIndex + 1} 上传失败，重试中...`, error);
        await new Promise(resolve => setTimeout(resolve, 2000));
        return this.uploadSingleChunk(fileObj, chunkIndex);
      }
    },

    // 合并分片
    async mergeChunks(fileObj, fileCount) {
      fileObj.statusText = '所有分片上传完成，正在合并文件...';
      fileObj.statusType = 'info';

      try {
        // 构建合并请求参数
        const mergeParams = {
          fileMd5: fileObj.fileMd5,
          order: fileObj.order,
          fileCount: fileCount,
          fileName: fileObj.file.name,
          fileSize: fileObj.file.size,
          totalChunks: fileObj.totalChunks,
          ...this.extraParams
        };

        console.log('合并请求参数:', mergeParams);

        const response = await this.axiosInstance.post(
          `${this.baseUrl}/merge`,
          null,
          {
            params: mergeParams
          }
        );

        fileObj.progress = 1;
        fileObj.uploadSuccess = true;
        fileObj.statusText = '文件上传成功';
        fileObj.statusType = 'success';

        // 按文件触发成功事件
        this.$emit('upload-success', {
          fileMd5: fileObj.fileMd5,
          fileName: fileObj.file.name,
          fileSize: fileObj.file.size,
          response: response.data,
          extraParams: this.extraParams
        });
      } catch (error) {
        console.error('文件合并失败详情:', error);
        const errorMsg = error.response?.data?.message || error.message || '未知错误';
        fileObj.statusText = `文件合并失败：${errorMsg}`;
        fileObj.statusType = 'error';
        this.$message.error(`文件合并失败：${errorMsg}`);
        this.$emit('upload-error', { error, extraParams: this.extraParams });
      }
    },

    // 取消上传
    cancelUpload() {
      if (this.speedInterval) {
        clearInterval(this.speedInterval);
        this.speedInterval = null;
      }
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('用户取消上传');
      }
      this.isUploading = false;
      this.files.forEach(fileObj => {
        fileObj.statusText = '上传已取消';
        fileObj.statusType = 'warning';
      });
      this.$message.info('上传已取消');
      this.$emit('upload-cancel', { extraParams: this.extraParams });
    },

    // 格式化文件大小
    formatFileSize(size) {
      if (!size) return '0 B';
      if (size < 1024) return `${size} B`;
      if (size < 1024 * 1024) return `${(size / 1024).toFixed(2)} KB`;
      if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(2)} MB`;
      return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
    },

    // 公开方法：只清除文件选择，不取消正在进行的上传
    clearFileSelection() {
      this.fileList = [];
      // 只清除没有开始上传的文件
      this.files = this.files.filter(fileObj => fileObj.isCalculatingMd5 || fileObj.uploadSuccess || this.isUploading);
    },

    // 公开方法：重置组件
    reset() {
      this.resetState();
    },

    // 公开方法：获取上传状态
    getUploadStatus() {
      return {
        files: this.files,
        isUploading: this.isUploading,
        activeRequests: this.activeRequests
      };
    }
  }
};
</script>

<style scoped>
.file-upload-wrapper {
  width: 100%;
}

.upload-progress {
  margin-top: 20px;
  padding: 20px;
  background: #f5f7fa;
  border-radius: 4px;
}

.file-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e4e7ed;
}

.file-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-right: 10px;
}

.file-size {
  font-size: 12px;
  color: #909399;
  white-space: nowrap;
}

.progress-label {
  font-size: 12px;
  color: #606266;
}

.status-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
}

.chunk-info {
  font-size: 12px;
  color: #909399;
}

.action-buttons {
  margin-top: 15px;
  text-align: center;
}

.action-buttons .el-button {
  margin: 0 5px;
}
</style>
